import { useMemo, useState } from 'react';
import { Link, useRoute } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, AlertCircle, Users, Dices } from 'lucide-react';
import { useActivationEvent } from '@/hooks/useActivationEvents';
import { useActivations } from '@/hooks/useActivations';
import {
  useEventSubmissions,
  useActivationGenerations,
  useActivationResults,
  useDrawRaffle,
} from '@/hooks/useActivationResults';
import {
  GenerationStatusBadge,
  DeliveryStatusBadge,
} from '@/components/activations/statusBadges';
import { ACTIVATION_TYPE_LABELS } from '@/types/activations';

export default function ActivationResultsPage() {
  const [, params] = useRoute('/activations/events/:eventId/results');
  const eventId = params?.eventId;

  const { data: event } = useActivationEvent(eventId);
  const { data: activations } = useActivations(eventId);
  const submissionsQuery = useEventSubmissions(eventId);
  const drawRaffle = useDrawRaffle();

  const [selectedActivationId, setSelectedActivationId] = useState<string>('');
  const selectedActivation = useMemo(
    () => (activations ?? []).find((a) => a.id === selectedActivationId),
    [activations, selectedActivationId],
  );

  const generationsQuery = useActivationGenerations(selectedActivationId || undefined);
  const resultsQuery = useActivationResults(selectedActivationId || undefined);

  return (
    <div className="flex-1" data-testid="activation-results-page">
      <Header
        title={event ? `Results — ${event.name}` : 'Results'}
        description="Submissions, generated media, delivery status, and leads."
      >
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${eventId}/leads`} data-testid="leads-link">
            <Users className="mr-2 h-4 w-4" />
            Leads
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link href={`/activations/events/${eventId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to event
          </Link>
        </Button>
      </Header>

      <div className="space-y-6 p-6">
        <Tabs defaultValue="submissions">
          <TabsList>
            <TabsTrigger value="submissions" data-testid="results-tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="media" data-testid="results-tab-media">Generated media</TabsTrigger>
            <TabsTrigger value="results" data-testid="results-tab-results">Results &amp; delivery</TabsTrigger>
          </TabsList>

          {/* ── Event-wide submissions ── */}
          <TabsContent value="submissions" className="pt-4">
            {submissionsQuery.isLoading && <Skeleton className="h-48 w-full" data-testid="submissions-loading" />}
            {submissionsQuery.isError && (
              <Alert variant="destructive" data-testid="submissions-error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Failed to load submissions</AlertTitle>
                <AlertDescription>{(submissionsQuery.error as Error)?.message}</AlertDescription>
              </Alert>
            )}
            {!submissionsQuery.isLoading && !submissionsQuery.isError && (
              (submissionsQuery.data?.length ?? 0) === 0 ? (
                <EmptyState message="No submissions yet." testId="submissions-empty" />
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Participant</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(submissionsQuery.data ?? []).map((s) => (
                        <TableRow key={s.id} data-testid={`submission-row-${s.id}`}>
                          <TableCell>{s.participantName || (s.userId ? '—' : 'Anonymous')}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.participantEmail ?? '—'}</TableCell>
                          <TableCell>{s.score ?? '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )
            )}
          </TabsContent>

          {/* ── Per-activation: media + results ── */}
          <TabsContent value="media" className="space-y-4 pt-4">
            <ActivationPicker
              activations={activations}
              value={selectedActivationId}
              onChange={setSelectedActivationId}
            />
            {!selectedActivationId && <EmptyState message="Select an activation to view its generated media." testId="media-no-selection" />}
            {selectedActivationId && generationsQuery.isLoading && <Skeleton className="h-48 w-full" data-testid="generations-loading" />}
            {selectedActivationId && generationsQuery.isError && (
              <Alert variant="destructive" data-testid="generations-error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Failed to load media</AlertTitle>
              </Alert>
            )}
            {selectedActivationId && !generationsQuery.isLoading && !generationsQuery.isError && (
              (generationsQuery.data?.length ?? 0) === 0 ? (
                <EmptyState message="No generated media for this activation." testId="generations-empty" />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {(generationsQuery.data ?? []).map((g) => (
                    <Card key={g.id} data-testid={`generation-card-${g.id}`}>
                      <div className="flex aspect-video items-center justify-center overflow-hidden rounded-t-lg bg-muted">
                        {g.outputBlobUrl || g.thumbnailUrl ? (
                          <img
                            src={g.thumbnailUrl || g.outputBlobUrl!}
                            alt="Generated media"
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">No output</span>
                        )}
                      </div>
                      <CardContent className="space-y-2 py-3">
                        <div className="flex items-center justify-between">
                          <GenerationStatusBadge status={g.generationStatus} />
                          {g.provider && <span className="text-xs text-muted-foreground">{g.provider}</span>}
                        </div>
                        {g.errorMessage && <p className="text-xs text-destructive">{g.errorMessage}</p>}
                        {(g.retryCount ?? 0) > 0 && <p className="text-xs text-muted-foreground">Retries: {g.retryCount}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            )}
          </TabsContent>

          {/* ── Per-activation: results + delivery ── */}
          <TabsContent value="results" className="space-y-4 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ActivationPicker
                activations={activations}
                value={selectedActivationId}
                onChange={setSelectedActivationId}
              />
              {selectedActivation?.activationType === 'RaffleDraw' && (
                <Button
                  onClick={() => drawRaffle.mutate(selectedActivationId)}
                  disabled={drawRaffle.isPending}
                  data-testid="draw-raffle-button"
                >
                  <Dices className="mr-2 h-4 w-4" />
                  {drawRaffle.isPending ? 'Drawing…' : 'Draw raffle'}
                </Button>
              )}
            </div>
            {!selectedActivationId && <EmptyState message="Select an activation to view its results." testId="results-no-selection" />}
            {selectedActivationId && resultsQuery.isLoading && <Skeleton className="h-48 w-full" data-testid="results-loading" />}
            {selectedActivationId && resultsQuery.isError && (
              <Alert variant="destructive" data-testid="results-error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Failed to load results</AlertTitle>
              </Alert>
            )}
            {selectedActivationId && !resultsQuery.isLoading && !resultsQuery.isError && (
              (resultsQuery.data?.length ?? 0) === 0 ? (
                <EmptyState message="No results yet for this activation." testId="results-empty" />
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Delivery</TableHead>
                        <TableHead>Via</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(resultsQuery.data ?? []).map((r) => (
                        <TableRow key={r.id} data-testid={`result-row-${r.id}`}>
                          <TableCell>{r.resultType}</TableCell>
                          <TableCell>
                            <DeliveryStatusBadge status={r.deliveryStatus} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.deliveredVia ?? '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ActivationPicker({
  activations,
  value,
  onChange,
}: {
  activations: ReturnType<typeof useActivations>['data'];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="max-w-sm">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="results-activation-picker">
          <SelectValue placeholder="Select an activation…" />
        </SelectTrigger>
        <SelectContent>
          {(activations ?? []).map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {ACTIVATION_TYPE_LABELS[a.activationType]} · {a.shortCode}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EmptyState({ message, testId }: { message: string; testId: string }) {
  return (
    <Card className="flex flex-col items-center gap-2 p-12 text-center" data-testid={testId}>
      <p className="text-sm text-muted-foreground">{message}</p>
    </Card>
  );
}
